import React, { useEffect, useState } from "react";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../../components/Loader/Loader";
import { Icon } from "@iconify/react";

const SystemConfigPage = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({
    maintenanceMode: {
      all: false,
      master: false,
      broker: false,
      user: false,
    },
    tradeStatus: true,
    appVersioning: {
      androidVersion: "",
      iosVersion: "",
      forceUpdate: false,
      updateUrl: "",
    },
    supportInfo: {
      whatsApp: "",
      telegram: "",
      supportEmail: "",
      phone: "",
    },
    globalNotice: "",
    branding: {
      appName: "ProTrade",
      copyrightText: "© ProTrade. All Rights Reserved.",
      logoUrl: "",
      faviconUrl: "",
    },
  });

  const { AsyncGetApiCall, AsyncPatchApiCall } = useApi();

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const result = await AsyncGetApiCall("/system-config");
      if (result?.success && result?.data) {
        setConfig(result.data);
      }
    } catch (error) {
      toast.error("Failed to fetch system configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleToggleChange = (path, value) => {
    const keys = path.split(".");
    setConfig((prev) => {
      const next = { ...prev };
      let current = next;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleInputChange = (path, value) => {
    const keys = path.split(".");
    setConfig((prev) => {
      const next = { ...prev };
      let current = next;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await AsyncPatchApiCall("/system-config/update", config);
      if (result?.success) {
        toast.success(result.message || "Configuration updated successfully");
      } else {
        toast.error(result?.message || "Failed to update configuration");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshCurrency = async () => {
    setLoading(true);
    try {
      const result = await AsyncGetApiCall("/system-config/refresh-currency");
      if (result?.success) {
        toast.success(`Currency rate updated to ${result.rate}`);
        fetchConfig();
      } else {
        toast.warn(result?.message || "Failed to refresh currency rate");
      }
    } catch (error) {
      toast.error("Something went wrong during refresh");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-24">
      {loading && <Loader />}
      <div className="d-flex align-items-center justify-content-between mb-24">
        <h4 className="mb-0">System Configuration</h4>
        <button
          type="button"
          className="btn btn-primary-600 radius-8 px-24"
          onClick={handleSubmit}
          disabled={loading}
        >
          Save Changes
        </button>
      </div>

      <div className="row gy-4">
        {/* Maintenance Mode Section */}
        <div className="col-lg-6">
          <div className="card h-100 radius-12 shadow-sm border-0">
            <div className="card-header bg-base border-bottom py-16 px-24">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Icon
                  icon="tabler:settings-automation"
                  className="text-primary text-xl"
                />
                Maintenance Mode Control
              </h6>
            </div>
            <div className="card-body p-24">
              <div className="d-flex flex-column gap-24">
                <div className="d-flex align-items-center justify-content-between p-16 radius-8 bg-danger-focus border-danger">
                  <div>
                    <h6 className="mb-0 text-danger-600">
                      Global Maintenance (ALL)
                    </h6>
                    <p className="text-sm text-secondary-light mb-0">
                      Shutdown all platforms immediately
                    </p>
                  </div>
                  <div className="form-check form-switch custom-switch-lg">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={config.maintenanceMode.all}
                      onChange={(e) =>
                        handleToggleChange(
                          "maintenanceMode.all",
                          e.target.checked,
                        )
                      }
                    />
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between p-12 radius-8 border">
                  <div>
                    <h6 className="mb-0 text-md">Master (Sub-Admin) Panel</h6>
                    <p className="text-xs text-secondary-light mb-0">
                      Maintenance for Master interface (Super-Admins can always
                      login)
                    </p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={config.maintenanceMode.master}
                      onChange={(e) =>
                        handleToggleChange(
                          "maintenanceMode.master",
                          e.target.checked,
                        )
                      }
                    />
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between p-12 radius-8 border">
                  <div>
                    <h6 className="mb-0 text-md">Broker Panel</h6>
                    <p className="text-xs text-secondary-light mb-0">
                      Maintenance for Broker interface
                    </p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={config.maintenanceMode.broker}
                      onChange={(e) =>
                        handleToggleChange(
                          "maintenanceMode.broker",
                          e.target.checked,
                        )
                      }
                    />
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between p-12 radius-8 border">
                  <div>
                    <h6 className="mb-0 text-md">User App / Website</h6>
                    <p className="text-xs text-secondary-light mb-0">
                      Maintenance for client-facing platforms
                    </p>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={config.maintenanceMode.user}
                      onChange={(e) =>
                        handleToggleChange(
                          "maintenanceMode.user",
                          e.target.checked,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Global Trading Section */}
        <div className="col-lg-6">
          <div className="card h-100 radius-12 shadow-sm border-0">
            <div className="card-header bg-base border-bottom py-16 px-24">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Icon
                  icon="tabler:chart-arrows"
                  className="text-primary text-xl"
                />
                Trading Control & Notice
              </h6>
            </div>
            <div className="card-body p-24">
              <div className="d-flex flex-column gap-24">
                <div className="d-flex align-items-center justify-content-between p-16 radius-8 bg-warning-focus border-warning">
                  <div>
                    <h6 className="mb-0 text-warning-700">
                      Global Trade Status
                    </h6>
                    <p className="text-sm text-secondary-light mb-0">
                      Enable or Disable execution for all users
                    </p>
                  </div>
                  <div className="form-check form-switch custom-switch-lg">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={config.tradeStatus}
                      onChange={(e) =>
                        handleToggleChange("tradeStatus", e.target.checked)
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label fw-medium">
                    Global Dashboard Notice
                  </label>
                  <textarea
                    className="form-control radius-8"
                    rows="4"
                    placeholder="Enter notice text for all users..."
                    value={config.globalNotice}
                    onChange={(e) =>
                      handleInputChange("globalNotice", e.target.value)
                    }
                  ></textarea>
                  <p className="text-xs text-secondary-light mt-1">
                    This text will appear as a marquee or popup on client apps.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* App Versioning Section - Temporarily Commented Out
        <div className="col-lg-6">
          <div className="card radius-12 shadow-sm border-0">
            <div className="card-header bg-base border-bottom py-16 px-24">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Icon
                  icon="tabler:device-mobile"
                  className="text-primary text-xl"
                />
                Mobile App Versioning
              </h6>
            </div>
            <div className="card-body p-24">
              <div className="row gy-3">
                <div className="col-sm-6">
                  <label className="form-label">Android Version</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="e.g. 1.0.5"
                    value={config.appVersioning.androidVersion}
                    onChange={(e) =>
                      handleInputChange(
                        "appVersioning.androidVersion",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div className="col-sm-6">
                  <label className="form-label">iOS Version</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="e.g. 1.0.2"
                    value={config.appVersioning.iosVersion}
                    onChange={(e) =>
                      handleInputChange(
                        "appVersioning.iosVersion",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Update Download URL</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="https://play.google.com/store/apps/..."
                    value={config.appVersioning.updateUrl}
                    onChange={(e) =>
                      handleInputChange(
                        "appVersioning.updateUrl",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div className="col-12 mt-3">
                  <div className="form-check form-switch d-flex align-items-center gap-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={config.appVersioning.forceUpdate}
                      onChange={(e) =>
                        handleToggleChange(
                          "appVersioning.forceUpdate",
                          e.target.checked,
                        )
                      }
                    />
                    <label className="form-check-label text-sm fw-medium">
                      Force Update (Block old versions)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        */}

        {/* Support Section - Temporarily Commented Out
        <div className="col-lg-6">
          <div className="card radius-12 shadow-sm border-0">
            <div className="card-header bg-base border-bottom py-16 px-24">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Icon icon="tabler:headset" className="text-primary text-xl" />
                Support & Contact Links
              </h6>
            </div>
            <div className="card-body p-24">
              <div className="row gy-3">
                <div className="col-sm-6">
                  <label className="form-label">WhatsApp Support</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="+91..."
                    value={config.supportInfo.whatsApp}
                    onChange={(e) =>
                      handleInputChange("supportInfo.whatsApp", e.target.value)
                    }
                  />
                </div>
                <div className="col-sm-6">
                  <label className="form-label">Telegram Support</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="@SupportUser"
                    value={config.supportInfo.telegram}
                    onChange={(e) =>
                      handleInputChange("supportInfo.telegram", e.target.value)
                    }
                  />
                </div>
                <div className="col-sm-6">
                  <label className="form-label">Support Email</label>
                  <input
                    type="email"
                    className="form-control radius-8"
                    placeholder="help@protrade.com"
                    value={config.supportInfo.supportEmail}
                    onChange={(e) =>
                      handleInputChange(
                        "supportInfo.supportEmail",
                        e.target.value,
                      )
                    }
                  />
                </div>
                <div className="col-sm-6">
                  <label className="form-label">Contact Phone</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="+91..."
                    value={config.supportInfo.phone}
                    onChange={(e) =>
                      handleInputChange("supportInfo.phone", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        */}
        {/* Currency Conversion Section */}
        <div className="col-12">
          <div className="card radius-12 shadow-sm border-0">
            <div className="card-header bg-base border-bottom py-16 px-24 d-flex align-items-center justify-content-between">
              <div>
                <h6 className="mb-0 d-flex align-items-center gap-2">
                  <Icon
                    icon="tabler:currency-dollar"
                    className="text-primary text-xl"
                  />
                  Currency Conversion Settings (USD to INR)
                </h6>
                <p className="text-xs text-secondary-light mb-0 mt-1">
                  Configure the rate for USD to INR conversion for Forex,
                  Crypto, etc.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-outline-primary radius-8 d-flex align-items-center gap-2"
                onClick={handleRefreshCurrency}
                disabled={loading}
              >
                <Icon icon="tabler:refresh" className="text-lg" />
                Refresh from Market
              </button>
            </div>
            <div className="card-body p-24">
              <div className="row gy-3 align-items-center">
                <div className="col-sm-6">
                  <div className="form-check form-switch d-flex align-items-center gap-2 p-12 radius-8 border">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={config.currencySettings?.isUsdToInrAuto || false}
                      onChange={(e) =>
                        handleToggleChange(
                          "currencySettings.isUsdToInrAuto",
                          e.target.checked,
                        )
                      }
                    />
                    <div>
                      <label className="form-check-label text-sm fw-medium mb-0">
                        Automatic USD Rate Update
                      </label>
                      <p className="text-xs text-secondary-light mb-0">
                        Fetch live rate from market data (USDINR) every 5 mins
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <label className="form-label fw-medium">
                    Manual USD to INR Rate
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-base border-end-0">
                      1 USD =
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control radius-8 border-start-0"
                      placeholder="e.g. 95.00"
                      disabled={config.currencySettings?.isUsdToInrAuto}
                      value={config.currencySettings?.usdToInrRate || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "currencySettings.usdToInrRate",
                          parseFloat(e.target.value),
                        )
                      }
                    />
                    <span className="input-group-text bg-base">INR</span>
                  </div>
                  {config.currencySettings?.isUsdToInrAuto && (
                    <p className="text-xs text-info mt-1">
                      Manual input is disabled while automatic update is ON.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Branding Section - Temporarily Commented Out
        <div className="col-12">
          <div className="card radius-12 shadow-sm border-0">
            <div className="card-header bg-base border-bottom py-16 px-24">
              <h6 className="mb-0 d-flex align-items-center gap-2">
                <Icon icon="tabler:palette" className="text-primary text-xl" />
                Branding & Appearance
              </h6>
              <p className="text-xs text-secondary-light mb-0 mt-1">
                Customize the platform name, copyright, and logos. Changes apply
                globally via WebSocket.
              </p>
            </div>
            <div className="card-body p-24">
              <div className="row gy-3">
                <div className="col-sm-6">
                  <label className="form-label">App / Platform Name</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="e.g. ProTrade"
                    value={config.branding?.appName || ""}
                    onChange={(e) =>
                      handleInputChange("branding.appName", e.target.value)
                    }
                  />
                  <p className="text-xs text-secondary-light mt-1">
                    Replaces the platform name across client apps.
                  </p>
                </div>
                <div className="col-sm-6">
                  <label className="form-label">Copyright Text</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="e.g. © 2025 YourBrand. All Rights Reserved."
                    value={config.branding?.copyrightText || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "branding.copyrightText",
                        e.target.value,
                      )
                    }
                  />
                  <p className="text-xs text-secondary-light mt-1">
                    Shown in footers across the platform.
                  </p>
                </div>
                <div className="col-sm-6">
                  <label className="form-label">Logo URL</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="https://cdn.example.com/logo.png"
                    value={config.branding?.logoUrl || ""}
                    onChange={(e) =>
                      handleInputChange("branding.logoUrl", e.target.value)
                    }
                  />
                  {config.branding?.logoUrl && (
                    <img
                      src={config.branding.logoUrl}
                      alt="Logo Preview"
                      className="mt-2 radius-8"
                      style={{ maxHeight: 48, objectFit: "contain" }}
                    />
                  )}
                </div>
                <div className="col-sm-6">
                  <label className="form-label">Favicon URL</label>
                  <input
                    type="text"
                    className="form-control radius-8"
                    placeholder="https://cdn.example.com/favicon.ico"
                    value={config.branding?.faviconUrl || ""}
                    onChange={(e) =>
                      handleInputChange("branding.faviconUrl", e.target.value)
                    }
                  />
                  {config.branding?.faviconUrl && (
                    <img
                      src={config.branding.faviconUrl}
                      alt="Favicon Preview"
                      className="mt-2 radius-8"
                      style={{ maxHeight: 32, objectFit: "contain" }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        */}
      </div>
    </div>
  );
};

export default SystemConfigPage;
